/* global $ */
import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from "../axiosInstance";
import Swal from "sweetalert2";
import { useNavigate, useLocation } from "react-router-dom";
import Footer from '../components/Footer';

function Addcontact() {
    const navigate = useNavigate();
    const location = useLocation();
    
    // Check if we're editing a contact
    const isEditMode = location.state?.isEdit || false;
    const contactToEdit = location.state?.contact || null;
    
    const [accounts, setAccounts] = useState([]);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);
    const [loading, setLoading] = useState(true);
    
    const [newRecord, setNewRecord] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        alternate_phone: "",
        whatsapp_no: "",
        designation: "",
        notes: "",
        account_id: "",
        isactive: true,
    });

    // Populate form with contact data if editing
    useEffect(() => {
        if (isEditMode && contactToEdit) {
            console.log("Edit contact data: ", contactToEdit);
            setNewRecord({
                first_name: contactToEdit.first_name || "",
                last_name: contactToEdit.last_name || "",
                email: contactToEdit.email || "",
                phone: contactToEdit.phone || "",
                alternate_phone: contactToEdit.alternate_phone || "",
                whatsapp_no: contactToEdit.whatsapp_no || "",
                designation: contactToEdit.designation || "",
                notes: contactToEdit.notes || "",
                account_id: contactToEdit.account_id || "",
                isactive: contactToEdit.isactive !== undefined ? contactToEdit.isactive : true,
            });
        }
    }, [isEditMode, contactToEdit]);

    const [errors, setErrors] = useState({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        alternate_phone: "",
        whatsapp_no: "",
        account_id: "",
    });

    const validateForm = () => {
        let formErrors = {};
        const phoneRegex = /^\d{10}$/;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!newRecord.first_name) {
            formErrors.first_name = "First Name is required";
        }

        if (!newRecord.last_name) {
            formErrors.last_name = "Last Name is required";
        }

        if (!newRecord.email) {
            formErrors.email = "Email is required";
        } else if (!emailRegex.test(newRecord.email)) {
            formErrors.email = 'Please enter a valid email address';
        }

        if (!newRecord.phone) {
            formErrors.phone = "Phone is required";
        } else if (!phoneRegex.test(newRecord.phone)) {
            formErrors.phone = 'Please enter a valid 10-digit phone number';
        }

        if (newRecord.alternate_phone && !phoneRegex.test(newRecord.alternate_phone)) {
            formErrors.alternate_phone = 'Please enter a valid 10-digit alternate phone number';
        }

        if (newRecord.whatsapp_no && !phoneRegex.test(newRecord.whatsapp_no)) {
            formErrors.whatsapp_no = 'Please enter a valid 10-digit WhatsApp number';
        }

        if (!newRecord.account_id) {
            formErrors.account_id = "Account is required";
        }

        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
            return false;
        }

        return true;
    };

    const select2InitRef = useRef(false);

    // Initialize Select2 when data is loaded
    useEffect(() => {
        if (accounts.length > 0) {
            setTimeout(() => {
                try {
                    // Check if jQuery and Select2 are available
                    if (typeof $ !== 'undefined' && $.fn.select2) {
                        // Destroy existing Select2 instances first
                        $(".select2").each(function () {
                            if ($(this).hasClass('select2-hidden-accessible')) {
                                $(this).select2('destroy');
                            }
                        });

                        // Initialize Select2 for all select elements
                        $(".select2").select2({
                            width: '100%',
                            placeholder: function() {
                                return $(this).find('option:first').text();
                            }
                        });

                        // Handle Select2 change events
                        $('.select2').off('select2:select').on('select2:select', function (e) {
                            const selectElement = e.target;
                            const event = new Event('change', { bubbles: true });
                            selectElement.dispatchEvent(event);
                        });

                        console.log("Select2 initialized successfully");
                        select2InitRef.current = true;
                    } else {
                        console.warn("jQuery or Select2 not available");
                    }
                } catch (error) {
                    console.error("Error initializing Select2:", error);
                }
            }, 500);
        }

        return () => {
            try {
                if (typeof $ !== 'undefined' && $.fn.select2) {
                    $('.select2').off('select2:select');
                    $(".select2").each(function () {
                        if ($(this).hasClass('select2-hidden-accessible')) {
                            $(this).select2('destroy');
                        }
                    });
                }
            } catch (error) {
                console.error("Error cleaning up Select2:", error);
            }
        };
    }, [accounts]);

    // Fetch Accounts
    const fetchAccounts = async () => {
        try {
            const response = await axiosInstance.get("/accounts/");
            // Filter only active accounts
            const activeAccounts = response.data.data.filter(account => account.isactive);
            setAccounts(activeAccounts);
            console.log("Accounts", activeAccounts);
        } catch (error) {
            console.error("Error fetching Accounts:", error);
        }
    };

    // Fetch all data on component mount
    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            try {
                await fetchAccounts();
            } catch (error) {
                console.error("Error loading data:", error);
            } finally {
                setLoading(false);
            }
        };
        
        loadData();
    }, []);

    // Handle Form Input Change
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setNewRecord({ ...newRecord, [name]: newValue });
        
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: "" }));
        }
    };

    const saveContact = async (event) => {
        event.preventDefault();

        if (isSaving || isSaved) return;

        if (validateForm()) {
            setIsSaving(true);
            setLoading(true);
            setErrors({});

            try {
                const created_by = localStorage.getItem('user_id');

                const updatedFormData = {
                    ...newRecord,
                    created_by: created_by,
                };
                
                console.log("Contact data to save:", updatedFormData);

                let formResponse;
                if (isEditMode && contactToEdit) {
                    // Update existing contact
                    formResponse = await axiosInstance.put(`/contacts/${contactToEdit.id}`, updatedFormData);
                    console.log("Contact updated:", formResponse.data);
                    Swal.fire('Updated!', 'Contact updated successfully', 'success');
                } else {
                    // Create new contact
                    formResponse = await axiosInstance.post('/contacts/', updatedFormData);
                    console.log("Contact created:", formResponse.data);
                    Swal.fire('Saved!', 'Contact saved successfully', 'success');
                }

                setIsSaved(true);
                navigate("/contact", { replace: true });

            } catch (error) {
                console.error('Error saving contact:', error);
                let errorMessage = isEditMode ? 'There was an issue in updating the contact.' : 'There was an issue in saving the contact.';
                if (error.response && error.response.data && error.response.data.message) {
                    errorMessage = error.response.data.message;
                }

                Swal.fire({
                    icon: 'error',
                    title: isEditMode ? 'Error Updating Contact!' : 'Error Saving Contact!',
                    text: errorMessage,
                });
            } finally {
                setLoading(false);
                setIsSaving(false);
            }
        }
    };

    return (
        <div>
            <div className="page-wrapper">
                <div className="content container-fluid">
                    <div className="card mb-0">
                        <div className="card-body">
                            <div className="page-header">
                                <div className="content-page-header">
                                    <h5>{isEditMode ? 'Edit Contact' : 'Add Contact'}</h5>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-md-12">
                                    <form onSubmit={saveContact}>
                                        {/* Basic Details */}
                                        <div className="form-group-item">
                                            <h5 className="form-title">Basic Details</h5>
                                            <div className="row">
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>First Name <span className="text-danger">*</span></label>
                                                        <input 
                                                            type="text" 
                                                            name="first_name" 
                                                            value={newRecord.first_name} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter First Name" 
                                                        />
                                                        {errors.first_name && <small className="text-danger">{errors.first_name}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Last Name <span className="text-danger">*</span></label>
                                                        <input 
                                                            type="text" 
                                                            name="last_name" 
                                                            value={newRecord.last_name} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter Last Name" 
                                                        />
                                                        {errors.last_name && <small className="text-danger">{errors.last_name}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Email <span className="text-danger">*</span></label>
                                                        <input 
                                                            type="email" 
                                                            name="email" 
                                                            value={newRecord.email} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter Email Address" 
                                                        />
                                                        {errors.email && <small className="text-danger">{errors.email}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Phone Number <span className="text-danger">*</span></label>
                                                        <input 
                                                            type="text" 
                                                            name="phone" 
                                                            value={newRecord.phone} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter Phone Number" 
                                                        />
                                                        {errors.phone && <small className="text-danger">{errors.phone}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Alternate Phone</label>
                                                        <input 
                                                            type="text" 
                                                            name="alternate_phone" 
                                                            value={newRecord.alternate_phone} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter Alternate Phone Number" 
                                                        />
                                                        {errors.alternate_phone && <small className="text-danger">{errors.alternate_phone}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>WhatsApp Number</label>
                                                        <input 
                                                            type="text" 
                                                            name="whatsapp_no" 
                                                            value={newRecord.whatsapp_no} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter WhatsApp Number" 
                                                        />
                                                        {errors.whatsapp_no && <small className="text-danger">{errors.whatsapp_no}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Designation</label>
                                                        <input 
                                                            type="text" 
                                                            name="designation" 
                                                            value={newRecord.designation} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter Designation" 
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Account <span className="text-danger">*</span></label>
                                                        <select
                                                            className="select2"
                                                            name="account_id"
                                                            value={newRecord.account_id}
                                                            onChange={handleChange}
                                                        >
                                                            <option value="">Select Account</option>
                                                            {accounts.map((account) => (
                                                                <option key={account.id} value={account.id}>
                                                                    {account.company_name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {errors.account_id && <small className="text-danger">{errors.account_id}</small>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Notes</label>
                                                        <textarea 
                                                            name="notes" 
                                                            value={newRecord.notes} 
                                                            onChange={handleChange} 
                                                            className="form-control" 
                                                            placeholder="Enter Notes"
                                                            rows="3"
                                                        />                                                        
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status */}
                                        <div className="form-group-item">
                                            <h5 className="form-title">Status</h5>
                                            <div className="row">
                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                    <div className="input-block mb-3">
                                                        <label>Contact Status</label>
                                                        <div className="form-check form-switch">
                                                            <input 
                                                                className="form-check-input" 
                                                                type="checkbox" 
                                                                name="isactive"
                                                                checked={newRecord.isactive}
                                                                onChange={handleChange}
                                                                id="contactStatus"
                                                            />
                                                            <label className="form-check-label" htmlFor="contactStatus">
                                                                {newRecord.isactive ? 'Active' : 'Inactive'}
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="add-customer-btns text-end">
                                            <a href="/contact" className="btn customer-btn-cancel">Cancel</a>
                                            <button 
                                                type="submit" 
                                                className="btn customer-btn-save" 
                                                disabled={isSaving || isSaved}
                                            >
                                                {isSaved ? (isEditMode ? 'Contact Updated' : 'Contact Saved') : 
                                                 isSaving ? (isEditMode ? 'Updating...' : 'Saving...') : 
                                                 (isEditMode ? 'Update Contact' : 'Save Contact')}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </div>
        </div>
    )
}

export default Addcontact;